import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useWidgetDispatch, useWidgetSelector, WidgetStore } from "../store/store";
import { useRouter } from "next/router";
import { setPaneNumber, setRecurring, setCauseAreaDistributionType, setCauseAreaSelection, setOrgAmount, setPrefilledShares, setReferralCode, prefillActions } from "../store/actions";
import { RecurringDonation, ShareType } from "../types/Enums";
import { WidgetContext } from "../../../../main/layout/layout";
import { PrefilledDistribution } from "../../../../main/layout/WidgetPane/WidgetPane";
import { CauseArea } from "../types/CauseArea";
import { WidgetProps } from "../types/WidgetProps";
import { paymentMethodConfigurations } from "../config/methods";
import { useDebouncedCallback } from "use-debounce";
import { State } from "../store/state";
import { getReferralCodeFromQuery, getStoredReferralCode, storeReferralCode } from "../../../../../util/referralCode";
import { splitAmountByShares } from "../utils/donationCalculations";
type WidgetDispatch = WidgetStore["dispatch"];

/** Designed pane content width, before the reserved scrollbar gutter. */
export const WIDGET_CONTENT_WIDTH = 576;
/** Must match `.widget::-webkit-scrollbar` so the gutter and thumb agree. */
const WIDGET_SCROLLBAR_WIDTH = 8;
export const WIDGET_FRAME_WIDTH = WIDGET_CONTENT_WIDTH + WIDGET_SCROLLBAR_WIDTH;
interface UsePrefilledDistributionProps {
  inline: boolean;
  causeAreas: CauseArea[] | undefined;
  prefilledDistribution: PrefilledDistribution | null;
}
export const usePrefilledCauseAreaIds = () => {
  const $ = _c(2);
  const [widgetContext] = useContext(WidgetContext);
  let t0;
  if ($[0] !== widgetContext.prefilled) {
    t0 = new Set(widgetContext.prefilled?.map(_temp) ?? []);
    $[0] = widgetContext.prefilled;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};

/**
 * Hook to handle prefilled distribution data for the widget
 */
export const usePrefilledDistribution = ({
  inline,
  causeAreas,
  prefilledDistribution
}: UsePrefilledDistributionProps) => {
  const dispatch = useWidgetDispatch();
  const [widgetContext] = useContext(WidgetContext);
  const causeAreaAmounts = useWidgetSelector((state: State) => state.donation.causeAreaAmounts) ?? {};
  // Add a ref to track if we've already applied the prefilled distribution
  const hasAppliedPrefill = useRef(false);
  useEffect(() => {
    // Return early if no cause areas to distribute
    if (!causeAreas || causeAreas.length === 0) {
      return;
    }

    // Skip if inline AND no direct prefilledDistribution was provided
    if (inline && !prefilledDistribution) {
      return;
    }

    // Use directly provided prefilledDistribution if available, otherwise fall back to context
    const prefilled = prefilledDistribution || (widgetContext.prefilled ? widgetContext.prefilled : []);

    // If no prefilled data available, return early
    if (prefilled.length === 0) {
      return;
    }

    // Only apply prefill once to avoid overwriting user changes
    // Except if the prefill comes from the context, then we need to reapply it
    if (hasAppliedPrefill.current && !widgetContext.prefilled) {
      return;
    }

    // Accumulate shares across every cause area into one flat org-keyed map (org IDs are
    // unique across cause areas) rather than dispatching setPrefilledShares per cause area,
    // which would otherwise have each cause area's dispatch clobber the previous one's.
    const combinedShares: Record<number, number> = {};
    let hasAnyPrefilledShares = false;
    const prefilledCauseAreas = causeAreas.filter(causeArea => prefilled.some(prefilledArea => prefilledArea.causeAreaId === causeArea.id));
    const visibleCauseAreas = causeAreas.filter(causeArea => causeArea.isActive || prefilledCauseAreas.some(prefilledArea => prefilledArea.id === causeArea.id));
    if (visibleCauseAreas.length > 1 && prefilledCauseAreas.length > 0) {
      const singlePrefilledCauseArea = prefilledCauseAreas.length === 1 ? prefilledCauseAreas[0] : undefined;
      dispatch(setCauseAreaSelection(singlePrefilledCauseArea ? "single" : "multiple", singlePrefilledCauseArea?.id));
      dispatch(setPaneNumber(1));
    }
    causeAreas.forEach(causeArea => {
      const prefilledCauseArea = prefilled.find(prefilledArea => prefilledArea.causeAreaId === causeArea.id);
      if (prefilledCauseArea) {
        const causeAreaAmount = causeAreaAmounts[causeArea.id] || 0;
        const sharesByOrgId = handlePrefilledCauseArea(dispatch, causeArea, prefilledCauseArea, causeAreaAmount);
        if (Object.keys(sharesByOrgId).length > 0) {
          hasAnyPrefilledShares = true;
          Object.assign(combinedShares, sharesByOrgId);
        }
      } else {
        resetCauseArea(dispatch, causeArea);
      }
    });
    dispatch(setPrefilledShares(hasAnyPrefilledShares ? combinedShares : null));

    // Mark that we've applied the prefill
    hasAppliedPrefill.current = true;
    // Deliberately excludes causeAreaAmounts - this effect should only re-run when the
    // prefill itself changes, not on every amount keystroke (the amount is only read to
    // seed the prefilled organization once).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inline, widgetContext.prefilled, causeAreas, prefilledDistribution, dispatch]);

  // Reset the ref if prefilled data changes
  useEffect(() => {
    hasAppliedPrefill.current = false;
  }, [prefilledDistribution, widgetContext.prefilled]);
};

/**
 * Sets the amount another block opened the widget pane with (WidgetContext's prefilledSum: the
 * tax deduction widget, the intervention widget, the wealth calculator), and one-time or monthly
 * when the block passes that too (prefilledRecurring: the wealth calculator, whose amount is a
 * monthly one; null leaves the donor's choice as is). Once per opening, so the
 * donor's own changes after that are kept. Opening the pane without an amount leaves the amounts
 * in the store (kept across page navigation) as they are, and only stops a cause area selected
 * later from getting the earlier amount. Runs after usePrefilledDistribution, so the amount goes
 * to the cause area a prefilled distribution selected.
 */
export const usePrefilledSum = t0 => {
  const $ = _c(6);
  const {
    inline,
    causeAreas
  } = t0;
  const dispatch = useWidgetDispatch();
  const [widgetContext] = useContext(WidgetContext);
  const appliedFor = useRef(null);
  let t1;
  let t2;
  if ($[0] !== causeAreas || $[1] !== dispatch || $[2] !== inline || $[3] !== widgetContext) {
    t1 = () => {
      if (inline || !causeAreas || causeAreas.length === 0) {
        return;
      }
      if (!widgetContext.open || appliedFor.current === widgetContext) {
        return;
      }
      appliedFor.current = widgetContext;
      for (const action of prefillActions(widgetContext)) {
        dispatch(action);
      }
    };
    t2 = [inline, causeAreas, widgetContext, dispatch];
    $[0] = causeAreas;
    $[1] = dispatch;
    $[2] = inline;
    $[3] = widgetContext;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
};

/**
 * Hook to handle URL query parameters for prefilling the widget
 */
export const useQueryParamsPrefill = t0 => {
  const $ = _c(12);
  const {
    inline,
    causeAreas,
    defaultPaymentType
  } = t0;
  const router = useRouter();
  const dispatch = useWidgetDispatch();
  const [widgetContext, setWidgetContext] = useContext(WidgetContext);
  const hasAppliedQueryParams = useRef(false);
  let t1;
  let t2;
  if ($[0] !== causeAreas || $[1] !== dispatch || $[2] !== inline || $[3] !== router.query || $[4] !== setWidgetContext || $[5] !== widgetContext) {
    t1 = () => {
      if (inline || !router.query || !causeAreas) {
        return;
      }
      if (hasAppliedQueryParams.current) {
        return;
      }
      const {
        distribution,
        recurring,
        referral,
        referralCode
      } = router.query;
      if (distribution && typeof distribution === "string") {
        const prefilledDistribution = parseDistributionQueryParam(distribution);
        setWidgetContext({
          open: true,
          prefilled: prefilledDistribution,
          prefilledSum: null,
          prefilledRecurring: null
        });
        hasAppliedQueryParams.current = true;
      }
      if (recurring !== undefined) {
        const recurringValue = Array.isArray(recurring) ? recurring[0] : recurring;
        const isTruthy = ["1", "true"].includes(recurringValue.toLowerCase());
        const isFalsy = ["0", "false"].includes(recurringValue.toLowerCase());
        if (isTruthy || isFalsy) {
          dispatch(setRecurring(isTruthy ? RecurringDonation.RECURRING : RecurringDonation.NON_RECURRING));
          setWidgetContext({
            ...widgetContext,
            open: true
          });
          hasAppliedQueryParams.current = true;
        }
      }
      const referralValue = getReferralCodeFromQuery({
        referral,
        referralCode
      });
      if (referralValue) {
        dispatch(setReferralCode(referralValue));
        storeReferralCode(referralValue);
        hasAppliedQueryParams.current = true;
      } else {
        const storedReferralCode = getStoredReferralCode();
        if (storedReferralCode) {
          dispatch(setReferralCode(storedReferralCode));
          hasAppliedQueryParams.current = true;
        }
      }
    };
    t2 = [inline, router.query, causeAreas, dispatch, setWidgetContext, widgetContext];
    $[0] = causeAreas;
    $[1] = dispatch;
    $[2] = inline;
    $[3] = router.query;
    $[4] = setWidgetContext;
    $[5] = widgetContext;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[8] !== defaultPaymentType || $[9] !== dispatch) {
    t3 = () => {
      dispatch(setRecurring(defaultPaymentType));
    };
    t4 = [defaultPaymentType, dispatch];
    $[8] = defaultPaymentType;
    $[9] = dispatch;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
};

// Helper functions

// Applies a prefilled cause area's organization shares (0-100 each, e.g. from the
// organizations list - one org at 100% - or a CMS-configured distribution link with
// several) as kr amounts of whatever the cause area's amount currently is, and returns
// the applied shares (org ID -> percentage) so the caller can track them for auto-updating
// as the amount changes later.
const handlePrefilledCauseArea = (dispatch: WidgetDispatch, causeArea: CauseArea, prefilledCauseArea: PrefilledDistribution[number], causeAreaAmount: number): Record<number, number> => {
  dispatch(setCauseAreaDistributionType(causeArea.id, causeArea.organizations.length <= 1 || prefilledCauseArea.organizations.length === 0 ? ShareType.STANDARD : ShareType.CUSTOM));
  const shareByOrgId: Record<number, number> = {};
  prefilledCauseArea.organizations.forEach(org => {
    if (org.share > 0) shareByOrgId[org.organizationId] = org.share;
  });
  const amountByOrgId = splitAmountByShares(causeAreaAmount, shareByOrgId);
  causeArea.organizations.forEach(organization => {
    dispatch(setOrgAmount(organization.id, amountByOrgId[organization.id] ?? 0));
  });
  return shareByOrgId;
};
const resetCauseArea = (dispatch: WidgetDispatch, causeArea: CauseArea) => {
  dispatch(setCauseAreaDistributionType(causeArea.id, ShareType.STANDARD));
  causeArea.organizations.forEach(organization => {
    dispatch(setOrgAmount(organization.id, 0));
  });
};
const parseDistributionQueryParam = (distribution: string): PrefilledDistribution => {
  return distribution.split(",").map(prefilledCauseArea => {
    const [causeAreaId, share, ...organizations] = prefilledCauseArea.split(":");
    return {
      causeAreaId: parseInt(causeAreaId),
      share: parseFloat(share),
      organizations: organizations.map(organization => {
        const [organizationId, share] = organization.split("-");
        return {
          organizationId: parseInt(organizationId),
          share: parseFloat(share)
        };
      })
    };
  });
};

/**
 * Determine available recurring options based on the configured payment methods
 */
export const useAvailableRecurringOptions = paymentMethods => {
  const $ = _c(7);
  let t0;
  if ($[0] !== paymentMethods) {
    t0 = paymentMethods.some(_temp2);
    $[0] = paymentMethods;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const recurring = t0;
  let t1;
  if ($[2] !== paymentMethods) {
    t1 = paymentMethods.some(_temp3);
    $[2] = paymentMethods;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const single = t1;
  let t2;
  if ($[4] !== recurring || $[5] !== single) {
    t2 = {
      recurring,
      single
    };
    $[4] = recurring;
    $[5] = single;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

/**
 * Determine available payment methods based on the selected recurring option
 */
export const useAvailablePaymentMethods = paymentMethods => {
  const $ = _c(5);
  const recurring = useWidgetSelector(_temp4);
  let t0;
  if ($[0] !== paymentMethods || $[1] !== recurring) {
    let t1;
    if ($[3] !== recurring) {
      t1 = method => {
        const configuration = paymentMethodConfigurations.find(config => config.id === method._id);
        return configuration?.recurringOptions.includes(recurring);
      };
      $[3] = recurring;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = paymentMethods.filter(t1);
    $[0] = paymentMethods;
    $[1] = recurring;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const availablePaymentMethods = t0;
  return availablePaymentMethods;
};

/**
 * This effect is used to set the default payment method to single if recurring is not enabled
 */
export const useDefaultPaymentMethodEffect = paymentMethods => {
  const $ = _c(5);
  const dispatch = useWidgetDispatch();
  const recurring = useWidgetSelector(_temp5);
  const availableRecurringOptions = useAvailableRecurringOptions(paymentMethods);
  let t0;
  let t1;
  if ($[0] !== availableRecurringOptions.recurring || $[1] !== dispatch || $[2] !== recurring) {
    t0 = () => {
      if (recurring === RecurringDonation.RECURRING && !availableRecurringOptions.recurring) {
        dispatch(setRecurring(RecurringDonation.NON_RECURRING));
      }
    };
    t1 = [recurring, availableRecurringOptions.recurring, dispatch];
    $[0] = availableRecurringOptions.recurring;
    $[1] = dispatch;
    $[2] = recurring;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
};

/**
 * Scale the widget to fit the screen
 */
export const useWidgetScaleEffect = (widgetRef: React.RefObject<HTMLDivElement | null>, inline: boolean) => {
  const [widgetContext] = useContext(WidgetContext);
  const [scalingFactor, setScalingFactor] = useState(1);
  const [scaledHeight, setScaledHeight] = useState(979);
  const [lastHeight, setLastHeight] = useState(979);
  const [lastWidth, setLastWidth] = useState(400);
  const scaleWidget = useCallback(() => {
    if (!inline || window.innerWidth < 1180) {
      setScalingFactor((window.innerWidth >= 1180 ? Math.min(window.innerWidth * 0.4, 720) : window.innerWidth) / WIDGET_CONTENT_WIDTH);
      setScaledHeight(Math.ceil(window.innerHeight / scalingFactor));
      if (window.innerHeight != lastHeight && window.innerWidth == lastWidth) {
        // This is probably the android keyboard opening
        const delta = lastHeight - window.innerHeight;
        if (delta > 0) widgetRef.current?.scrollTo(0, Math.ceil(delta / scalingFactor));else widgetRef.current?.scrollTo(0, 0);
      }
      setLastWidth(window.innerWidth);
      setLastHeight(window.innerHeight);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- adding or removing deps changes how often the widget rescales
  }, [setScalingFactor, setScaledHeight, scalingFactor, scaledHeight, setLastWidth, setLastHeight, inline]);
  const debouncedScaleWidget = useDebouncedCallback(() => scaleWidget(), 1000, {
    maxWait: 1000
  });
  useEffect(() => {
    if (!inline) {
      window.addEventListener("resize", debouncedScaleWidget);
      return () => {
        window.removeEventListener("resize", debouncedScaleWidget);
      };
    }
  }, [debouncedScaleWidget, inline]);
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- scales the widget when it opens or its context changes
    scaleWidget();
  }, [widgetContext, scaleWidget]);
  return useMemo(() => ({
    scaledHeight,
    scalingFactor
  }), [scaledHeight, scalingFactor]);
};
function _temp(area) {
  return area.causeAreaId;
}
function _temp2(method) {
  const configuration = paymentMethodConfigurations.find(config => config.id === method._id);
  return configuration?.recurringOptions.includes(RecurringDonation.RECURRING);
}
function _temp3(method_0) {
  const configuration_0 = paymentMethodConfigurations.find(config_0 => config_0.id === method_0._id);
  return configuration_0?.recurringOptions.includes(RecurringDonation.NON_RECURRING);
}
function _temp4(state) {
  return state.donation.recurring;
}
function _temp5(state) {
  return state.donation.recurring;
}